import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2, Play, RotateCcw, Save, Pause } from "lucide-react";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { useRecordingUpload } from "@/hooks/useRecordingUpload";
import { AudioWaveform } from "./AudioWaveform";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import type { ContentItem } from "@/types/content";
import type { RecordingState } from "../MediaContentViewer";

interface RecordingInterfaceProps {
  content: ContentItem;
  onRecordingComplete: () => void;
  onRecordingStateChange?: (state: RecordingState) => void;
}

// Format seconds as MM:SS
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
export function RecordingInterface({
  content,
  onRecordingComplete,
  onRecordingStateChange
}: RecordingInterfaceProps) {
  const {
    recordingState,
    currentTime,
    audioLevel,
    audioBlob,
    permissionStatus,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    requestPermission,
    resetRecording
  } = useAudioRecording();

  // Notify parent of recording state changes
  useEffect(() => {
    onRecordingStateChange?.(recordingState as RecordingState);
  }, [recordingState, onRecordingStateChange]);
  const {
    uploadRecording,
    isUploading,
    uploadProgress
  } = useRecordingUpload();
  const audioPreviewRef = useRef<HTMLAudioElement>(null);
  const savedRef = useRef(false);
  const contentIdRef = useRef(content.id);
  contentIdRef.current = content.id;

  // Clean up orphan content row on unmount if recording was never saved
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!savedRef.current) {
        // Best-effort cleanup on tab/browser close via sendBeacon
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/content?id=eq.${contentIdRef.current}`;
        navigator.sendBeacon?.(url); // sendBeacon doesn't support DELETE, so we also rely on unmount cleanup
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (!savedRef.current) {
        // Delete orphan recording row when navigating away
        supabase.from("content").delete().eq("id", contentIdRef.current).then(({ error }) => {
          if (error) console.error("Failed to clean up orphan recording:", error);
        });
      }
    };
  }, []);

  // Create object URL for audio preview
  const audioPreviewUrl = audioBlob ? URL.createObjectURL(audioBlob) : null;

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (audioPreviewUrl) {
        URL.revokeObjectURL(audioPreviewUrl);
      }
    };
  }, [audioPreviewUrl]);
  const handleSaveRecording = async () => {
    if (!audioBlob) return;
    const success = await uploadRecording(audioBlob, content.id);
    if (success) {
      savedRef.current = true;
      onRecordingComplete();
    }
  };
  const handlePlayPreview = () => {
    if (audioPreviewRef.current) {
      audioPreviewRef.current.play();
    }
  };
  const isRecording = recordingState === "recording";
  const isPaused = recordingState === "paused";
  const isActive = isRecording || isPaused;
  const isStopped = recordingState === "stopped";
  const isRequesting = recordingState === "requesting";
  const needsPermission = permissionStatus === "prompt" || permissionStatus === "unknown";
  return <div className="w-full rounded-lg">
      <div className="flex w-full items-center gap-2 rounded-2xl border border-border bg-card p-2 pr-3">
        {/* Main action button - never shrink */}
        <div className="shrink-0">
          {isStopped ? <div className="flex items-center gap-1 sm:gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button onClick={handleSaveRecording} disabled={isUploading} size="icon" variant="ghost" className="h-8 w-8 sm:h-9 sm:w-9 bg-green-400/10 hover:bg-green-400/20 dark:bg-green-500/10 dark:hover:bg-green-500/20">
                    {isUploading ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-green-500/60 dark:text-green-500/80" /> : <Save className="h-4 w-4 shrink-0 text-green-500/60 hover:text-green-400 dark:text-green-500/80 dark:hover:text-green-500" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isUploading ? `Saving... ${uploadProgress}%` : "Save recording"}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={handlePlayPreview} disabled={!audioPreviewUrl} className="h-8 w-8 sm:h-9 sm:w-9 bg-purple-400/10 hover:bg-purple-400/20 dark:bg-purple-500/10 dark:hover:bg-purple-500/20">
                    <Play className="h-4 w-4 shrink-0 text-purple-500/60 hover:text-purple-400 dark:text-purple-500/80 dark:hover:text-purple-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Replay recording</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={resetRecording} disabled={isUploading} className="h-8 w-8 sm:h-9 sm:w-9 bg-orange-400/10 hover:bg-orange-400/20 dark:bg-orange-500/10 dark:hover:bg-orange-500/20">
                    <RotateCcw className="h-4 w-4 shrink-0 text-orange-500/60 hover:text-orange-400 dark:text-orange-500/80 dark:hover:text-orange-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset recording</p>
                </TooltipContent>
              </Tooltip>
            </div> : isActive ? <div className="flex items-center gap-1 sm:gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={isRecording ? pauseRecording : resumeRecording} 
                    className="h-8 w-8 sm:h-9 sm:w-9 bg-blue-400/10 hover:bg-blue-400/20 dark:bg-blue-500/10 dark:hover:bg-blue-500/20"
                  >
                    {isRecording ? (
                      <Pause className="h-4 w-4 shrink-0 text-blue-500/60 hover:text-blue-400 dark:text-blue-500/80 dark:hover:text-blue-500" />
                    ) : (
                      <Play className="h-4 w-4 shrink-0 text-blue-500/60 hover:text-blue-400 dark:text-blue-500/80 dark:hover:text-blue-500" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isRecording ? "Pause recording" : "Resume recording"}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={stopRecording} 
                    className="h-8 w-8 sm:h-9 sm:w-9 bg-red-400/10 hover:bg-red-400/20 dark:bg-red-500/10 dark:hover:bg-red-500/20"
                  >
                    <Square className="h-4 w-4 shrink-0 text-red-500/60 hover:text-red-400 dark:text-red-500/80 dark:hover:text-red-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Stop recording</p>
                </TooltipContent>
              </Tooltip>
            </div> : <Button onClick={startRecording} disabled={isRequesting} className="flex items-center gap-1 rounded-2xl px-2 sm:gap-2 sm:px-4">
              {isRequesting && <Loader2 className="h-4 w-4 animate-spin" />}
              <span className="hidden sm:inline">Start Recording</span>
              <span className="sm:hidden">Record</span>
            </Button>}
        </div>

        {/* Waveform visualization - flexible, shrinks to fit */}
        <div className="min-w-0 flex-1">
          <AudioWaveform audioLevel={audioLevel} isActive={isActive} />
        </div>

        {/* Timer - never shrink */}
        <div className="shrink-0 text-right text-sm font-normal text-foreground/70 sm:text-base">
          <span>{formatTime(currentTime)}</span>
        </div>
      </div>

      {/* Microphone permission prompt */}
      {needsPermission && recordingState === "idle" && <div className="ml-1 flex justify-start pr-2 pt-4">
          <Button variant="ghost" onClick={requestPermission} className="flex items-center gap-2">
            <Mic className="h-4 w-4" />
            <span className="text-sm">Allow Microphone Access</span>
          </Button>
        </div>}

      {/* Permission denied message */}
      {permissionStatus === "denied" && <div className="ml-1 flex justify-start pr-2 pt-4">
          <p className="text-sm text-destructive">
            Microphone access was denied. Please allow microphone access in your browser settings and refresh the page.
          </p>
        </div>}

      {/* Hidden audio element for preview */}
      {audioPreviewUrl && <audio ref={audioPreviewRef} src={audioPreviewUrl} className="hidden" />}
    </div>;
}
